/**
 * OsContactForm — MADE-304
 *
 * Sailmaking inquiry form for offshore-sails.com/contact.
 * Fields: Name, Email, Phone, Boat Name, Boat Make/Model, LOA,
 *         Sail(s) needed (multi-select), Timeline, Message.
 * Captcha: Altcha proof-of-work widget (challengeurl → /api/os/altcha-challenge).
 * Submission: POST /api/os/contact → Resend → info@offshore-sails.com.
 * UX: spinner during submit, inline field errors, success state.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Divider,
  FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel,
  Grid, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Send as SendIcon } from '@mui/icons-material';

// ── TypeScript: declare the <altcha-widget> custom element ───────────────────
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'altcha-widget': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        challengeurl?: string;
        name?: string;
        auto?: string;
        hidefooter?: boolean | string;
        hidelogo?: boolean | string;
        style?: React.CSSProperties;
      };
    }
  }
}

// ── Constants ─────────────────────────────────────────────────────────────────

const SAIL_OPTIONS = ['Mainsail', 'Genoa/Jib', 'Spinnaker', 'Code Zero', 'Other'];

const TIMELINE_OPTIONS = [
  { value: 'asap',          label: 'As soon as possible' },
  { value: '1-3-months',   label: '1–3 months' },
  { value: '3-6-months',   label: '3–6 months' },
  { value: 'researching',  label: 'Just researching' },
];

// ── Types ─────────────────────────────────────────────────────────────────────

interface FormState {
  name: string;
  email: string;
  phone: string;
  boatName: string;
  boatModel: string;
  loa: string;
  sails: string[];
  timeline: string;
  message: string;
}

const INITIAL_FORM: FormState = {
  name: '', email: '', phone: '',
  boatName: '', boatModel: '', loa: '',
  sails: [], timeline: '', message: '',
};

// ── Component ─────────────────────────────────────────────────────────────────

export const OsContactForm: React.FC = () => {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [serverError, setServerError] = useState('');
  const [altchaVerified, setAltchaVerified] = useState(false);

  const altchaRef = useRef<HTMLElement & { value?: string; state?: string }>(null);

  // Load the altcha web component — registers <altcha-widget> custom element
  useEffect(() => {
    import('altcha').catch(err =>
      console.warn('[OsContactForm] altcha web component unavailable:', err)
    );
  }, []);

  // Track altcha verified state
  useEffect(() => {
    const el = altchaRef.current;
    if (!el) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ state?: string }>).detail ?? {};
      setAltchaVerified(detail.state === 'verified');
      // Clear altcha error on verify
      if (detail.state === 'verified') {
        setErrors(prev => { const n = { ...prev }; delete n.altcha; return n; });
      }
    };
    el.addEventListener('statechange', handler);
    return () => el.removeEventListener('statechange', handler);
  });

  // ── Field helpers ──────────────────────────────────────────────────────────

  const setField = (field: keyof FormState, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const toggleSail = (sail: string) => {
    setForm(prev => ({
      ...prev,
      sails: prev.sails.includes(sail)
        ? prev.sails.filter(s => s !== sail)
        : [...prev.sails, sail],
    }));
    if (errors.sails) setErrors(prev => { const n = { ...prev }; delete n.sails; return n; });
  };

  // ── Validation ─────────────────────────────────────────────────────────────

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!form.name.trim())                                           e.name    = 'Name is required.';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
                                                                     e.email   = 'A valid email address is required.';
    if (!form.message.trim())                                        e.message = 'Please tell us about your project.';
    if (!altchaVerified)                                             e.altcha  = 'Please complete the security check.';
    return e;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Scroll to first error (scrollIntoView may be absent in test environments)
      const firstKey = Object.keys(validationErrors)[0];
      const firstEl = document.querySelector(`[data-field="${firstKey}"]`);
      (firstEl as HTMLElement & { scrollIntoView?: (opts?: ScrollIntoViewOptions) => void })?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitState('submitting');
    setServerError('');

    const altchaPayload = (altchaRef.current as HTMLElement & { value?: string })?.value ?? '';

    try {
      const resp = await fetch('/api/os/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          boatName: form.boatName.trim() || undefined,
          boatModel: form.boatModel.trim() || undefined,
          loa: form.loa.trim() || undefined,
          sails: form.sails,
          timeline: form.timeline || undefined,
          message: form.message.trim(),
          altcha: altchaPayload,
        }),
      });

      const data = await resp.json() as { success: boolean; field?: string; error?: string; message?: string };

      if (!resp.ok || !data.success) {
        if (data.field) {
          setErrors({ [data.field]: data.error ?? 'Please check this field.' });
          setSubmitState('idle');
        } else {
          setServerError(data.error ?? 'Something went wrong. Please try again.');
          setSubmitState('error');
        }
        return;
      }

      setSubmitState('success');
    } catch {
      setServerError('Unable to send your inquiry. Please check your connection and try again.');
      setSubmitState('error');
    }
  };

  // ── Success state ──────────────────────────────────────────────────────────

  if (submitState === 'success') {
    return (
      <Box
        data-testid="contact-success"
        sx={{
          textAlign: 'center',
          py: 6,
          px: 2,
          bgcolor: 'white',
          borderRadius: 3,
          border: '1px solid rgba(0,38,128,0.10)',
          boxShadow: '0 4px 20px rgba(0,38,128,0.07)',
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 56, color: '#002680', mb: 2 }} />
        <Typography variant="h5" sx={{ color: '#002680', fontWeight: 700, mb: 1.5 }}>
          Inquiry Received
        </Typography>
        <Typography variant="body1" sx={{ color: '#555', maxWidth: 420, mx: 'auto', lineHeight: 1.75 }}>
          Thank you. We have received your inquiry and will be in touch shortly at{' '}
          <strong>{form.email}</strong>.
        </Typography>
      </Box>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      data-testid="os-contact-form"
    >
      {submitState === 'error' && serverError && (
        <Alert severity="error" sx={{ mb: 3 }} data-testid="contact-server-error">
          {serverError}
        </Alert>
      )}

      <Grid container spacing={3}>

        {/* Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Name"
            required
            fullWidth
            value={form.name}
            onChange={e => setField('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            inputProps={{ 'data-field': 'name', 'data-testid': 'field-name' }}
            disabled={submitState === 'submitting'}
          />
        </Grid>

        {/* Email */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Email"
            type="email"
            required
            fullWidth
            value={form.email}
            onChange={e => setField('email', e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            inputProps={{ 'data-field': 'email', 'data-testid': 'field-email' }}
            disabled={submitState === 'submitting'}
          />
        </Grid>

        {/* Phone */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Phone (optional)"
            type="tel"
            fullWidth
            value={form.phone}
            onChange={e => setField('phone', e.target.value)}
            inputProps={{ 'data-testid': 'field-phone' }}
            disabled={submitState === 'submitting'}
          />
        </Grid>

        {/* Boat Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Boat Name (optional)"
            fullWidth
            value={form.boatName}
            onChange={e => setField('boatName', e.target.value)}
            inputProps={{ 'data-testid': 'field-boatName' }}
            disabled={submitState === 'submitting'}
          />
        </Grid>

        {/* Boat Make/Model */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Boat Make / Model (optional)"
            fullWidth
            value={form.boatModel}
            onChange={e => setField('boatModel', e.target.value)}
            inputProps={{ 'data-testid': 'field-boatModel' }}
            disabled={submitState === 'submitting'}
          />
        </Grid>

        {/* LOA */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Length Overall (LOA) (optional)"
            fullWidth
            placeholder="e.g. 48ft or 14.6m"
            value={form.loa}
            onChange={e => setField('loa', e.target.value)}
            inputProps={{ 'data-testid': 'field-loa' }}
            disabled={submitState === 'submitting'}
          />
        </Grid>

        {/* Sails needed */}
        <Grid item xs={12}>
          <Divider sx={{ mb: 2, borderColor: 'rgba(0,38,128,0.08)' }} />
          <FormControl component="fieldset" error={!!errors.sails}>
            <FormLabel
              component="legend"
              sx={{ color: '#002680', fontWeight: 600, mb: 1 }}
            >
              Sail(s) needed (select all that apply)
            </FormLabel>
            <FormGroup row>
              {SAIL_OPTIONS.map(sail => (
                <FormControlLabel
                  key={sail}
                  control={
                    <Checkbox
                      checked={form.sails.includes(sail)}
                      onChange={() => toggleSail(sail)}
                      size="small"
                      sx={{ color: '#002680', '&.Mui-checked': { color: '#002680' } }}
                      inputProps={{ 'data-testid': `sail-${sail.toLowerCase().replace(/[^a-z]/g, '-')}` } as React.InputHTMLAttributes<HTMLInputElement>}
                      disabled={submitState === 'submitting'}
                    />
                  }
                  label={sail}
                />
              ))}
            </FormGroup>
            {errors.sails && <FormHelperText>{errors.sails}</FormHelperText>}
          </FormControl>
        </Grid>

        {/* Timeline */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="timeline-label">Timeline</InputLabel>
            <Select
              labelId="timeline-label"
              label="Timeline"
              value={form.timeline}
              onChange={e => setField('timeline', e.target.value)}
              inputProps={{ 'data-testid': 'field-timeline' }}
              disabled={submitState === 'submitting'}
            >
              {TIMELINE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Message */}
        <Grid item xs={12}>
          <TextField
            label="Message"
            required
            fullWidth
            multiline
            rows={5}
            placeholder="Tell us about your boat, your sailing, and what you need from your sails…"
            value={form.message}
            onChange={e => setField('message', e.target.value)}
            error={!!errors.message}
            helperText={errors.message}
            inputProps={{ 'data-field': 'message', 'data-testid': 'field-message' }}
            disabled={submitState === 'submitting'}
          />
        </Grid>

        {/* Altcha captcha */}
        <Grid item xs={12}>
          <Box data-field="altcha">
            <altcha-widget
              ref={altchaRef}
              challengeurl="/api/os/altcha-challenge"
              auto="onfocus"
              hidefooter
              hidelogo
              style={{
                '--altcha-color-base': '#002680',
                '--altcha-color-text': '#002680',
                '--altcha-color-border-focus': '#FFC726',
              } as React.CSSProperties}
            />
            {errors.altcha && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, display: 'block' }}
                data-testid="altcha-error"
              >
                {errors.altcha}
              </Typography>
            )}
          </Box>
        </Grid>

      </Grid>

      {/* Submit */}
      <Stack direction="row" justifyContent="flex-end" mt={4}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={submitState === 'submitting'}
          data-testid="contact-submit"
          startIcon={submitState === 'submitting' ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
          sx={{
            bgcolor: '#002680',
            color: 'white',
            fontWeight: 700,
            px: 5,
            py: 1.5,
            textTransform: 'none',
            fontSize: '1rem',
            '&:hover': { bgcolor: '#001a60' },
            '&:disabled': { bgcolor: 'rgba(0,38,128,0.4)' },
          }}
        >
          {submitState === 'submitting' ? 'Sending…' : 'Send Inquiry'}
        </Button>
      </Stack>
    </Box>
  );
};
